import { useCallback, useState } from "react"
import { useNavigate, Navigate, useLocation } from "react-router"
import { AnimatePresence, motion } from "framer-motion"
import { useAuth } from "@/lib/auth"
import { apiClient } from "@/lib/api"
import { trackEvent } from "@/lib/posthog"

// ---- Types ----

type WizardStep = "connect-pms" | "importing" | "done"
type PmsSubStep = "key-entry" | "unit-select"

interface PmsUnit {
  id: string
  name: string
  rooms?: number
  image_url?: string
}

interface OnboardingState {
  step: WizardStep
  pmsSubStep: PmsSubStep
  pmsProvider: string
  apiKey: string
  guestyClientId: string
  guestyClientSecret: string
  propertyId: string
  units: PmsUnit[]
  selectedUnitIds: Set<string>
  tosAccepted: boolean
  // Result
  createdProperties: { id: string; name: string }[]
  // Importing
  importProgress: number
  importStatus: string
  importCurrentUnit: string
  // UI
  loading: boolean
  error: string
}

const INITIAL_STATE: OnboardingState = {
  step: "connect-pms",
  pmsSubStep: "key-entry",
  pmsProvider: "",
  apiKey: "",
  guestyClientId: "",
  guestyClientSecret: "",
  propertyId: "",
  units: [],
  selectedUnitIds: new Set(),
  tosAccepted: false,
  createdProperties: [],
  importProgress: 0,
  importStatus: "",
  importCurrentUnit: "",
  loading: false,
  error: "",
}

const PMS_PROVIDERS = [
  { value: "lodgify", label: "Lodgify", description: "Properties, bookings and guest data", available: true },
  { value: "hostfully", label: "Hostfully", description: "Properties, bookings and guest data", available: true },
  { value: "guesty", label: "Guesty", description: "OAuth2 — Client ID + Secret", available: true },
  { value: "hostaway", label: "Hostaway", description: "OAuth2 — Account ID + API Key", available: true },
  { value: "ownerrez", label: "OwnerRez", description: "Basic Auth — Email + Personal Access Token", available: true },
  { value: "smoobu", label: "Smoobu", description: "Properties, bookings and guest data", available: true },
]

const COMING_SOON_PMS = [
  { id: "hospitable", name: "Hospitable" },
]

const fadeVariants = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -8 },
}

// ---- Branded Header ----

function OnboardingHeader({
  title,
  subtitle,
}: {
  title: string
  subtitle: string
}) {
  return (
    <div className="onboarding-header">
      <p className="onboarding-header__brand">STAYFRAME</p>
      <h1 className="onboarding-header__title">{title}</h1>
      <p className="onboarding-header__subtitle">{subtitle}</p>
    </div>
  )
}

// ---- Step 1: Connect PMS ----

function ConnectPmsStep({
  pmsSubStep,
  pmsProvider,
  apiKey,
  guestyClientId,
  guestyClientSecret,
  propertyIdFilter,
  units,
  selectedUnitIds,
  tosAccepted,
  onChangeApiKey,
  onChangeGuestyClientId,
  onChangeGuestyClientSecret,
  onChangePropertyId,
  onSelectProvider,
  onToggleUnit,
  onSelectAll,
  onChangeTos,
  onFetch,
  onImport,
  onBack,
  onLogout,
  loading,
  error,
}: {
  pmsSubStep: PmsSubStep
  pmsProvider: string
  apiKey: string
  guestyClientId: string
  guestyClientSecret: string
  propertyIdFilter: string
  units: PmsUnit[]
  selectedUnitIds: Set<string>
  tosAccepted: boolean
  onChangeApiKey: (v: string) => void
  onChangeGuestyClientId: (v: string) => void
  onChangeGuestyClientSecret: (v: string) => void
  onChangePropertyId: (v: string) => void
  onSelectProvider: (v: string) => void
  onToggleUnit: (id: string) => void
  onSelectAll: () => void
  onChangeTos: (v: boolean) => void
  onFetch: () => void
  onImport: () => void
  onBack: () => void
  onLogout: () => void
  loading: boolean
  error: string
}) {
  const providerLabel = PMS_PROVIDERS.find((p) => p.value === pmsProvider)?.label || "PMS"
  const allSelected = units.length > 0 && selectedUnitIds.size === units.length

  return (
    <div className="onboarding-container">
      <OnboardingHeader
        title={pmsSubStep === "key-entry" ? "Connect your properties" : "Choose properties"}
        subtitle={
          pmsSubStep === "key-entry"
            ? "We'll import your property details and upcoming bookings."
            : `We found ${units.length} propert${units.length === 1 ? "y" : "ies"} in your ${providerLabel} account. Select which to import.`
        }
      />

      <div className="onboarding-card">
        {pmsSubStep === "key-entry" && (
          <>
            <div className="onboarding-pms-list">
              {PMS_PROVIDERS.map((pms) => (
                <div
                  key={pms.value}
                  className={`onboarding-pms-option ${pmsProvider === pms.value ? "onboarding-pms-option--active" : ""}`}
                  onClick={() => onSelectProvider(pms.value)}
                  style={{ cursor: "pointer" }}
                >
                  <div className="onboarding-pms-option__icon">{pms.label[0]}</div>
                  <div className="onboarding-pms-option__info">
                    <p className="onboarding-pms-option__name">{pms.label}</p>
                    <p className="onboarding-pms-option__desc">{pms.description}</p>
                  </div>
                  {pmsProvider === pms.value && (
                    <svg className="onboarding-pms-option__check" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
              ))}
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault()
                onFetch()
              }}
              className="onboarding-pms-form"
            >
              {pmsProvider === "guesty" || pmsProvider === "hostaway" || pmsProvider === "ownerrez" ? (
                <>
                  <div className="onboarding-field">
                    <label className="onboarding-field__label" htmlFor="ob-guesty-client-id">
                      {pmsProvider === "ownerrez" ? "Email" : pmsProvider === "hostaway" ? "Account ID" : "Client ID"}
                    </label>
                    <input
                      id="ob-guesty-client-id"
                      className={`onboarding-field__input${error ? " onboarding-field__input--error" : ""}`}
                      type={pmsProvider === "ownerrez" ? "email" : "text"}
                      placeholder={pmsProvider === "ownerrez" ? "you@example.com" : pmsProvider === "hostaway" ? "Hostaway Account ID" : "Guesty OAuth2 Client ID"}
                      value={guestyClientId}
                      onChange={(e) => onChangeGuestyClientId(e.target.value)}
                      disabled={loading}
                      autoFocus
                    />
                  </div>
                  <div className="onboarding-field">
                    <label className="onboarding-field__label" htmlFor="ob-guesty-client-secret">
                      {pmsProvider === "ownerrez" ? "Personal Access Token" : pmsProvider === "hostaway" ? "API Key" : "Client Secret"}
                    </label>
                    <input
                      id="ob-guesty-client-secret"
                      className={`onboarding-field__input${error ? " onboarding-field__input--error" : ""}`}
                      type="password"
                      placeholder={pmsProvider === "ownerrez" ? "pt_..." : pmsProvider === "hostaway" ? "Hostaway API Key" : "Guesty OAuth2 Client Secret"}
                      value={guestyClientSecret}
                      onChange={(e) => onChangeGuestyClientSecret(e.target.value)}
                      disabled={loading}
                    />
                    <div className="onboarding-field__hint">
                      {pmsProvider === "ownerrez"
                        ? "Found in your OwnerRez account under Settings → API Access."
                        : pmsProvider === "hostaway"
                          ? "Found in your Hostaway Dashboard under API credentials."
                          : "Found in your Guesty Developer Portal under API credentials."}
                    </div>
                  </div>
                </>
              ) : (
                <div className="onboarding-field">
                  <label className="onboarding-field__label" htmlFor="ob-api-key">
                    API key
                  </label>
                  <input
                    id="ob-api-key"
                    className={`onboarding-field__input${error ? " onboarding-field__input--error" : ""}`}
                    type="password"
                    placeholder="Paste your API key"
                    value={apiKey}
                    onChange={(e) => onChangeApiKey(e.target.value)}
                    disabled={loading}
                    autoFocus
                  />
                  <div className="onboarding-field__hint">
                    Found in your {providerLabel} account settings.
                  </div>
                </div>
              )}

              <div className="onboarding-field">
                <label className="onboarding-field__label" htmlFor="ob-property-id">
                  Property ID <span className="onboarding-field__optional">(optional)</span>
                </label>
                <input
                  id="ob-property-id"
                  className="onboarding-field__input"
                  type="text"
                  placeholder="Leave blank to import all"
                  value={propertyIdFilter}
                  onChange={(e) => onChangePropertyId(e.target.value)}
                  disabled={loading}
                />
              </div>

              <div className="onboarding-data-disclosure">
                <p className="onboarding-data-disclosure__title">What Stayframe will access</p>
                <ul className="onboarding-data-disclosure__list">
                  <li>Property details (name, address, images)</li>
                  <li>Booking data (dates, guest names, revenue)</li>
                  <li>Availability calendar</li>
                </ul>
                <p className="onboarding-data-disclosure__note">
                  Read-only access. Stayframe never modifies your {providerLabel} data.
                </p>
              </div>

              <label className="onboarding-tos-check">
                <input
                  type="checkbox"
                  className="onboarding-tos-check__input"
                  checked={tosAccepted}
                  onChange={(e) => onChangeTos(e.target.checked)}
                  disabled={loading}
                />
                <span className="onboarding-tos-check__text">
                  I agree to Stayframe's{" "}
                  <a href="/terms" target="_blank" rel="noopener noreferrer" className="onboarding-tos-check__link">
                    Terms of Service
                  </a>{" "}
                  and confirm I'm authorised to share this data from my {providerLabel} account.
                </span>
              </label>

              {error && <div className="onboarding-field__error">{error}</div>}

              <button
                type="submit"
                className="onboarding-btn onboarding-btn--primary onboarding-btn--full"
                disabled={!pmsProvider || ((pmsProvider === "guesty" || pmsProvider === "hostaway" || pmsProvider === "ownerrez") ? (!guestyClientId.trim() || !guestyClientSecret.trim()) : !apiKey.trim()) || !tosAccepted || loading}
              >
                {loading ? "Connecting..." : "Connect and import"}
              </button>
            </form>

            <div className="onboarding-pms-coming">
              <p className="onboarding-pms-coming__label">
                Also coming:{" "}
                {COMING_SOON_PMS.map((pms, i) => (
                  <span key={pms.id}>
                    {pms.name}{i < COMING_SOON_PMS.length - 1 ? ", " : ""}
                  </span>
                ))}
              </p>
            </div>
          </>
        )}

        {pmsSubStep === "unit-select" && (
          <>
            <div className="onboarding-units-header">
              <button
                type="button"
                className="onboarding-link-btn"
                onClick={onSelectAll}
              >
                {allSelected ? "Deselect all" : "Select all"}
              </button>
              <span className="onboarding-units-count">
                {selectedUnitIds.size} of {units.length} selected
              </span>
            </div>

            <div className="onboarding-units">
              {units.map((unit) => {
                const isSelected = selectedUnitIds.has(unit.id)
                return (
                  <button
                    key={unit.id}
                    type="button"
                    className={`onboarding-unit${isSelected ? " onboarding-unit--selected" : ""}`}
                    onClick={() => onToggleUnit(unit.id)}
                  >
                    <div
                      className={`onboarding-unit__checkbox${isSelected ? " onboarding-unit__checkbox--checked" : ""}`}
                    >
                      {isSelected && (
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                    <div className="onboarding-unit__info">
                      <div className="onboarding-unit__name">{unit.name}</div>
                      <div className="onboarding-unit__id">ID: {unit.id}</div>
                    </div>
                  </button>
                )
              })}
            </div>
            {error && <div className="onboarding-field__error" style={{ marginTop: 12 }}>{error}</div>}

            <button
              type="button"
              className="onboarding-btn onboarding-btn--primary onboarding-btn--full"
              disabled={selectedUnitIds.size === 0 || loading}
              onClick={onImport}
              style={{ marginTop: "var(--sf-space-4)" }}
            >
              {loading
                ? "Creating..."
                : selectedUnitIds.size === 1
                  ? "Import Property"
                  : `Import ${selectedUnitIds.size} Properties`}
            </button>
          </>
        )}
      </div>

      <div className="onboarding-footer-links">
        {pmsSubStep === "unit-select" ? (
          <button type="button" className="onboarding-link-btn" onClick={onBack} disabled={loading}>
            Back
          </button>
        ) : (
          <button type="button" className="onboarding-link-btn" onClick={onLogout}>
            Sign out
          </button>
        )}
      </div>
    </div>
  )
}

// ---- Step 2: Importing ----

const IMPORT_STEPS_PMS = [
  { key: "creating", label: "Creating property" },
  { key: "syncing_bookings", label: "Syncing bookings" },
  { key: "syncing_calendar", label: "Syncing calendar" },
  { key: "generating_guidebook", label: "Generating guidebook" },
  { key: "complete", label: "Complete" },
] as const

function ImportingStep({
  progress,
  status,
  currentUnit,
  totalUnits,
  completedUnits,
  createdProperties,
}: {
  progress: number
  status: string
  currentUnit: string
  totalUnits: number
  completedUnits: number
  createdProperties: { id: string; name: string }[]
}) {
  const steps = IMPORT_STEPS_PMS
  const currentIdx = steps.findIndex((s) => s.key === status)
  const activeStep = steps[currentIdx >= 0 ? currentIdx : 0]

  const statusText =
    status === "complete"
      ? "Import complete"
      : `${activeStep?.label || "Setting up"}...`

  const isMulti = totalUnits > 1

  return (
    <div className="onboarding-container">
      <OnboardingHeader
        title={status === "complete" ? "Everything's in" : "Importing your data"}
        subtitle={
          status === "complete"
            ? isMulti
              ? `${createdProperties.length} properties are ready to go.`
              : createdProperties[0]
                ? `${createdProperties[0].name} is ready to go.`
                : "Your properties are ready."
            : isMulti
              ? `Property ${completedUnits + 1} of ${totalUnits}${currentUnit ? ` — ${currentUnit}` : ""}`
              : "This will only take a moment."
        }
      />

      <div className="onboarding-card">
        <div className="onboarding-progress">
          <div className="onboarding-progress__track">
            <motion.div
              className="onboarding-progress__fill"
              initial={{ width: "0%" }}
              animate={{ width: `${progress}%` }}
              transition={{ ease: "easeOut", duration: 0.3 }}
            />
          </div>
          <div className="onboarding-progress__status">
            <p className="onboarding-progress__text">{statusText}</p>
            {status !== "complete" ? (
              <div className="onboarding-progress__dots">
                {[0, 1, 2].map((i) => (
                  <motion.div
                    key={i}
                    className="onboarding-progress__dot"
                    animate={{ opacity: [0.3, 1, 0.3] }}
                    transition={{ duration: 1, repeat: Infinity, delay: i * 0.2 }}
                  />
                ))}
              </div>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--sf-primary)" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            )}
          </div>
        </div>

        <div className="onboarding-import-steps">
          {steps.map((step, idx) => {
            const isDone = currentIdx > idx || status === "complete"
            const isActive = currentIdx === idx && status !== "complete"
            return (
              <div
                key={step.key}
                className={`onboarding-import-step${isDone ? " onboarding-import-step--done" : ""}${isActive ? " onboarding-import-step--active" : ""}`}
              >
                <div className="onboarding-import-step__icon">
                  {isDone ? (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  ) : isActive ? (
                    <motion.div
                      className="onboarding-import-step__spinner"
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    />
                  ) : (
                    <div className="onboarding-import-step__pending" />
                  )}
                </div>
                <span className="onboarding-import-step__label">{step.label}</span>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

// ---- Step 3: You're In ----

function YoureInStep({
  createdProperties,
  onGoToDashboard,
}: {
  createdProperties: { id: string; name: string }[]
  onGoToDashboard: () => void
}) {
  const isMulti = createdProperties.length > 1
  return (
    <div className="onboarding-container onboarding-container--center">
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="onboarding-done"
      >
        <p className="onboarding-header__brand">STAYFRAME</p>

        <div className="onboarding-done__check">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>

        <h1 className="onboarding-header__title">You're in</h1>
        <p className="onboarding-header__subtitle">
          {isMulti ? (
            <>
              Your {createdProperties.length} properties are ready.
              Stayframe will sync your calendars and start building your guest guides.
            </>
          ) : (
            <>
              Your property <strong>{createdProperties[0]?.name || "property"}</strong> is ready.
              Stayframe will sync your calendar and start building your guest guide.
            </>
          )}
        </p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3, delay: 0.3 }}
      >
        <button
          type="button"
          className="onboarding-btn onboarding-btn--primary onboarding-btn--lg"
          onClick={onGoToDashboard}
        >
          Go to your dashboard
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 12h14M12 5l7 7-7 7" />
          </svg>
        </button>
      </motion.div>
    </div>
  )
}

// ---- Main Onboarding Page ----

export function OnboardingPage() {
  const { properties, logout, refreshSession, switchProperty } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [state, setState] = useState<OnboardingState>(INITIAL_STATE)
  const isAddMode = new URLSearchParams(location.search).get("mode") === "add"

  // Redirect if user already has properties (but let "done" step render)
  if (properties.length > 0 && !isAddMode && state.step !== "done") {
    return <Navigate to="/" replace />
  }

  const update = useCallback(
    (patch: Partial<OnboardingState>) => setState((s) => ({ ...s, ...patch })),
    [],
  )

  // ---- PMS path handlers ----
  const handleFetchUnits = async () => {
    update({ loading: true, error: "" })
    try {
      // Guesty/Hostaway/OwnerRez: serialize dual credentials as JSON; others use plain API key
      const effectiveKey = state.pmsProvider === "guesty"
        ? JSON.stringify({ clientId: state.guestyClientId.trim(), clientSecret: state.guestyClientSecret.trim() })
        : state.pmsProvider === "hostaway"
          ? JSON.stringify({ accountId: state.guestyClientId.trim(), apiKey: state.guestyClientSecret.trim() })
          : state.pmsProvider === "ownerrez"
            ? JSON.stringify({ email: state.guestyClientId.trim(), token: state.guestyClientSecret.trim() })
            : state.apiKey.trim()
      const res = await apiClient.fetchPmsUnits(effectiveKey, state.pmsProvider)
      if (!res.ok) {
        update({ loading: false, error: res.error || "Failed to fetch properties. Check your API key." })
        return
      }
      const units = (res.units || []).map((u: Record<string, unknown>) => ({
        id: String(u.id || u.property_id || ""),
        name: String(u.name || u.property_name || `Unit ${u.id}`),
        rooms: Number(u.rooms || 0),
        image_url: String(u.image_url || ""),
      }))
      if (units.length === 0) {
        update({ loading: false, error: "No properties found in your account." })
        return
      }
      // Auto-select all units
      const allIds = new Set(units.map((u: PmsUnit) => u.id))
      update({ loading: false, units, selectedUnitIds: allIds, pmsSubStep: "unit-select" })
    } catch {
      update({ loading: false, error: "Network error. Please try again." })
    }
  }

  const handleToggleUnit = (id: string) => {
    setState((s) => {
      const next = new Set(s.selectedUnitIds)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return { ...s, selectedUnitIds: next }
    })
  }

  const handleSelectAll = () => {
    setState((s) => {
      const allSelected = s.selectedUnitIds.size === s.units.length
      return {
        ...s,
        selectedUnitIds: allSelected ? new Set<string>() : new Set(s.units.map((u) => u.id)),
      }
    })
  }

  const handleImportSelected = async () => {
    const selectedUnits = state.units.filter((u) => state.selectedUnitIds.has(u.id))
    if (selectedUnits.length === 0) return

    const totalUnits = selectedUnits.length
    const createdProperties: { id: string; name: string }[] = []

    update({ step: "importing", importProgress: 0, importStatus: "creating", error: "" })
    trackEvent("property_import_started", { provider: state.pmsProvider, unit_count: selectedUnits.length })

    for (let i = 0; i < selectedUnits.length; i++) {
      const unit = selectedUnits[i]
      const unitBase = (i / totalUnits) * 100
      const unitSlice = 100 / totalUnits

      const prog = (pct: number) => Math.round(unitBase + unitSlice * pct)

      update({
        importProgress: prog(0.05),
        importStatus: "creating",
        importCurrentUnit: unit.name,
      })

      try {
        const effectiveKey = state.pmsProvider === "guesty"
          ? JSON.stringify({ clientId: state.guestyClientId.trim(), clientSecret: state.guestyClientSecret.trim() })
          : state.pmsProvider === "hostaway"
            ? JSON.stringify({ accountId: state.guestyClientId.trim(), apiKey: state.guestyClientSecret.trim() })
            : state.pmsProvider === "ownerrez"
              ? JSON.stringify({ email: state.guestyClientId.trim(), token: state.guestyClientSecret.trim() })
              : state.apiKey.trim()
        const res = await apiClient.createPropertyFromUnit({
          unit_id: unit.id,
          unit_name: unit.name,
          pms_provider: state.pmsProvider,
          public_api_key: effectiveKey,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
          image_url: unit.image_url || "",
        })

        if (!res.ok) {
          // Skip failed unit but continue with the rest
          continue
        }

        const property = res.property || { id: "", name: unit.name }
        createdProperties.push(property)

        // Sync bookings
        update({ importProgress: prog(0.25), importStatus: "syncing_bookings" })
        if (property.id) {
          await apiClient.syncPerformanceData(property.id).catch(() => {})
        }

        // Sync calendar
        update({ importProgress: prog(0.50), importStatus: "syncing_calendar" })
        if (property.id) {
          await apiClient.syncCalendar(property.id).catch(() => {})
        }

        // Guidebook generation
        update({ importProgress: prog(0.70), importStatus: "generating_guidebook" })
        if (property.id) {
          await apiClient.runResearch({ trigger: "onboarding" }, property.id).catch(() => {})
        }

        // Save onboarding timestamp
        if (property.id) {
          await apiClient.savePropertySettings(
            { onboarding_completed_at: new Date().toISOString() },
            property.id,
          ).catch(() => {})
        }

        update({ importProgress: prog(1.0), createdProperties: [...createdProperties] })
      } catch {
        // Skip failed unit but continue with the rest
      }
    }

    if (createdProperties.length === 0) {
      update({
        step: "connect-pms",
        pmsSubStep: "unit-select",
        importProgress: 0,
        importStatus: "",
        importCurrentUnit: "",
        error: "Failed to import properties. Please try again.",
      })
      return
    }

    update({
      importProgress: 100,
      importStatus: "complete",
      importCurrentUnit: "",
      createdProperties,
    })
    trackEvent("onboarding_completed", { provider: state.pmsProvider, property_count: createdProperties.length })
    setTimeout(() => update({ step: "done" }), 600)
  }

  const handleGoToDashboard = async () => {
    await refreshSession()
    const first = state.createdProperties[0]
    if (first?.id) {
      await switchProperty(first.id).catch(() => {})
    }
    navigate("/", { replace: true })
  }

  // ---- Render ----

  return (
    <div className="onboarding-page">
      <AnimatePresence mode="wait">
        {state.step === "connect-pms" && (
          <motion.div key="connect-pms" variants={fadeVariants} initial="initial" animate="animate" exit="exit" transition={{ duration: 0.2 }} className="onboarding-motion-wrap">
            <ConnectPmsStep
              pmsSubStep={state.pmsSubStep}
              pmsProvider={state.pmsProvider}
              apiKey={state.apiKey}
              guestyClientId={state.guestyClientId}
              guestyClientSecret={state.guestyClientSecret}
              propertyIdFilter={state.propertyId}
              units={state.units}
              selectedUnitIds={state.selectedUnitIds}
              tosAccepted={state.tosAccepted}
              onChangeApiKey={(v) => update({ apiKey: v })}
              onChangeGuestyClientId={(v) => update({ guestyClientId: v })}
              onChangeGuestyClientSecret={(v) => update({ guestyClientSecret: v })}
              onChangePropertyId={(v) => update({ propertyId: v })}
              onSelectProvider={(v) => update({ pmsProvider: v })}
              onToggleUnit={handleToggleUnit}
              onSelectAll={handleSelectAll}
              onChangeTos={(v) => update({ tosAccepted: v })}
              onFetch={handleFetchUnits}
              onImport={handleImportSelected}
              onBack={() => {
                if (state.pmsSubStep === "unit-select") {
                  update({ pmsSubStep: "key-entry", error: "" })
                }
              }}
              onLogout={logout}
              loading={state.loading}
              error={state.error}
            />
          </motion.div>
        )}

        {state.step === "importing" && (
          <motion.div key="importing" variants={fadeVariants} initial="initial" animate="animate" exit="exit" transition={{ duration: 0.2 }} className="onboarding-motion-wrap">
            <ImportingStep
              progress={state.importProgress}
              status={state.importStatus}
              currentUnit={state.importCurrentUnit}
              totalUnits={state.selectedUnitIds.size}
              completedUnits={state.createdProperties.length}
              createdProperties={state.createdProperties}
            />
          </motion.div>
        )}

        {state.step === "done" && (
          <motion.div key="done" variants={fadeVariants} initial="initial" animate="animate" exit="exit" transition={{ duration: 0.2 }} className="onboarding-motion-wrap">
            <YoureInStep
              createdProperties={state.createdProperties}
              onGoToDashboard={handleGoToDashboard}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
